import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import CabinCrewLayout from '../layouts/CabinCrewLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Target, Trophy, Clock, Calendar, ChevronRight, ArrowLeft,
  Award, TrendingUp, CheckCircle, XCircle, AlertCircle, Play,
  FileText, BarChart3, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CabinCrewMyExams() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [examResults, setExamResults] = useState([]);
  const [canTakeExam, setCanTakeExam] = useState(true);
  const [daysUntilNextExam, setDaysUntilNextExam] = useState(0);

  useEffect(() => {
    loadExamResults();
  }, []);

  const loadExamResults = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/exam-history`, {
        withCredentials: true
      });
      setExamResults(response.data.exams || []);
      setCanTakeExam(response.data.can_take_exam);
      setDaysUntilNextExam(response.data.days_until_next_exam || 0);
    } catch (error) {
      console.error('Error loading exam results:', error);
      // If endpoint doesn't exist, try alternative
      try {
        const altResponse = await axios.get(`${API_URL}/api/admin/all-results`, {
          withCredentials: true
        });
        // Filter for current user
        const userExams = (altResponse.data || []).filter(
          exam => exam.user_id === user?.id || exam.user_email === user?.email
        );
        setExamResults(userExams);
        
        // Check if can take exam based on last exam date
        if (userExams.length > 0) {
          const lastExam = userExams[0];
          const lastExamDate = new Date(lastExam.timestamp);
          const daysSinceLastExam = Math.floor((Date.now() - lastExamDate.getTime()) / (1000 * 60 * 60 * 24));
          setCanTakeExam(daysSinceLastExam >= 30);
          setDaysUntilNextExam(Math.max(0, 30 - daysSinceLastExam));
        } else {
          setCanTakeExam(true);
          setDaysUntilNextExam(0);
        }
      } catch (altError) {
        console.error('Alternative also failed:', altError);
        setExamResults([]);
        setCanTakeExam(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      'A1': 'bg-green-500',
      'A2': 'bg-blue-500',
      'B1': 'bg-yellow-500',
      'B2': 'bg-orange-500',
      'C1': 'bg-red-500',
      'C2': 'bg-purple-500'
    };
    return colors[level] || 'bg-gray-500';
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleStartExam = () => {
    if (canTakeExam) {
      navigate('/exam/english');
    } else {
      toast.error(`Sınavı tekrar yapabilmek için ${daysUntilNextExam} gün beklemeniz gerekmektedir.`);
    }
  };

  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Sınavlar yükleniyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }

  return (
    <CabinCrewLayout>
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-8">
        <div className="container mx-auto px-6 max-w-4xl">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="rounded-xl"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-black text-gray-800">Sınavlarım</h1>
              <p className="text-gray-500">Geçmiş sınav sonuçlarınız ve seviye bilgileriniz</p>
            </div>
          </div>

          {/* Current Level Card */}
          <Card className="mb-6 bg-gradient-to-r from-sky-500 to-indigo-600 text-white border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                    <Trophy className="w-8 h-8" />
                  </div>
                  <div>
                    <p className="text-sky-100 text-sm">Mevcut Seviyeniz</p>
                    <p className="text-3xl font-black">
                      {examResults.length > 0 ? examResults[0].level : 'Belirlenmedi'}
                    </p>
                    {examResults.length > 0 && (
                      <p className="text-sky-100 text-sm">
                        Son sınav: {formatDate(examResults[0].timestamp)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sky-100 text-sm">Toplam Sınav</p>
                  <p className="text-4xl font-black">{examResults.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Start Exam Button */}
          <Card className="mb-6 border-2 border-dashed border-gray-200">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                    canTakeExam 
                      ? 'bg-gradient-to-br from-emerald-400 to-emerald-600' 
                      : 'bg-gray-300'
                  }`}>
                    {canTakeExam ? (
                      <Play className="w-7 h-7 text-white" />
                    ) : (
                      <Clock className="w-7 h-7 text-gray-500" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {canTakeExam ? 'Seviye Tespit Sınavı' : 'Sınav Bekleme Süresi'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {canTakeExam 
                        ? examResults.length === 0 
                          ? 'İlk sınavınızı yaparak seviyenizi belirleyin'
                          : 'Seviyenizi yeniden test edin'
                        : `${daysUntilNextExam} gün sonra tekrar sınav yapabilirsiniz`
                      }
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleStartExam}
                  disabled={!canTakeExam}
                  className={canTakeExam 
                    ? 'bg-emerald-500 hover:bg-emerald-600' 
                    : 'bg-gray-300 cursor-not-allowed'
                  }
                >
                  {canTakeExam ? 'Sınava Başla' : `${daysUntilNextExam} Gün`}
                  {canTakeExam && <ChevronRight className="w-4 h-4 ml-1" />}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Exam History */}
          <div className="mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-500" />
              Sınav Geçmişi
            </h2>
          </div>

          {examResults.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-10 h-10 text-gray-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Henüz Sınav Yapılmadı</h3>
                <p className="text-gray-500 mb-6">
                  İlk seviye tespit sınavınızı yaparak İngilizce seviyenizi öğrenin.
                </p>
                <Button 
                  onClick={() => navigate('/exam/english')}
                  className="bg-sky-500 hover:bg-sky-600"
                >
                  <Play className="w-4 h-4 mr-2" />
                  İlk Sınavımı Yap
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {examResults.map((exam, index) => (
                <Card 
                  key={exam.id || index} 
                  className={`border-0 shadow-sm hover:shadow-md transition-all cursor-pointer ${
                    index === 0 ? 'ring-2 ring-sky-500' : ''
                  }`}
                  onClick={() => exam.id && navigate(`/results/${exam.id}`)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Rank Badge */}
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                          index === 0 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                          index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400' :
                          index === 2 ? 'bg-gradient-to-br from-amber-600 to-amber-700' :
                          'bg-gray-200'
                        }`}>
                          {index === 0 ? (
                            <Star className="w-6 h-6 text-white" />
                          ) : (
                            <span className="text-lg font-bold text-white">#{index + 1}</span>
                          )}
                        </div>

                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={`${getLevelColor(exam.level)} text-white`}>
                              {exam.level}
                            </Badge>
                            {index === 0 && (
                              <Badge className="bg-sky-100 text-sky-700">En Son</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            <Calendar className="w-3 h-3 inline mr-1" />
                            {formatDate(exam.timestamp)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-6">
                        {/* Score */}
                        <div className="text-center">
                          <p className={`text-2xl font-black ${getScoreColor(exam.score)}`}>
                            %{Math.round(exam.score)}
                          </p>
                          <p className="text-xs text-gray-400">Puan</p>
                        </div>

                        {/* Correct/Wrong */}
                        <div className="flex items-center gap-3">
                          <div className="text-center">
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-4 h-4" />
                              <span className="font-bold">{exam.correct_count || '-'}</span>
                            </div>
                            <p className="text-xs text-gray-400">Doğru</p>
                          </div>
                          <div className="text-center">
                            <div className="flex items-center gap-1 text-red-500">
                              <XCircle className="w-4 h-4" />
                              <span className="font-bold">{exam.wrong_count || '-'}</span>
                            </div>
                            <p className="text-xs text-gray-400">Yanlış</p>
                          </div>
                        </div>

                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Info Card */}
          <Card className="mt-6 bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-amber-800">Sınav Kuralları</h4>
                  <ul className="text-sm text-amber-700 mt-1 space-y-1">
                    <li>• Her sınav 30 sorudan oluşur (A1'den C2'ye)</li>
                    <li>• Sınav süresi 30 dakikadır</li>
                    <li>• İki sınav arasında 30 gün beklemeniz gerekmektedir</li>
                    <li>• Seviyeniz doğru cevap sayınıza göre belirlenir</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </CabinCrewLayout>
  );
}
